"use client";
import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useFormik } from "formik";

import { Button, Input, FormRow } from "@/components/common";
import { isEmptyValue } from "@/utils/isEmptyValue";
import { useTranslations } from "next-intl";
import {
  useLoginRequest,
  useSendParcelLoginRequest,
} from "@/api/useLoginRequest";
import { TLoginInitialData } from "@/types/apiParam.types";
import { Theme } from "@/enums";
import { useTheme } from "@/context/Theme.context";
import logAnalyticsEvent from "@/utils/logAnalyticsEvent";

const LoginForm = () => {
  const router = useRouter();
  const theme = useTheme();

  const t = useTranslations();
  const login = useLoginRequest();
  const sendParcelLogin = useSendParcelLoginRequest();

  const {
    values,
    setSubmitting,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    errors,
    setErrors,
  } = useFormik({
    initialValues: {
      email: "",
      password: "",
    } as TLoginInitialData,
    onSubmit: async (values) => {
      const isPhone = !values.email.includes("@") && theme === Theme.UZ;

      if (isEmptyValue(values.email)) {
        setErrors({ email: "Email is required" });
        return;
      }

      if (isEmptyValue(values.password)) {
        setErrors({ password: "Password is required" });
        return;
      }

      const payload = isPhone
        ? { phone: values.email, password: values.password }
        : { email: values.email, password: values.password };

      try {
        setSubmitting(true);
        const response = await login(payload);
        localStorage.setItem("token", response?.data?.data?.token);

        if (theme === Theme.GEORGIA) {
          logAnalyticsEvent("log_in");
        }

        router.push("/parcels");
      } catch (error) {
        if (error?.status === 400) {
          if (isPhone) {
            const errorMessage =
              error?.response?.data?.error?.error_fields?.[0]?.phone;
            setErrors({ email: errorMessage || "validations_phone_invalid" });
            return;
          }

          const errorMessage = error?.response?.data?.error?.message;

          setErrors({ email: errorMessage || "" });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isFormDisabled = isSubmitting;

  return (
    <form onSubmit={handleSubmit}>
      <FormRow className="mt-[2.19rem]">
        <Input
          label={
            theme === Theme.UZ
              ? "enter_your_email_or_phone"
              : "enter_your_email"
          }
          error={errors?.email}
          {...getFieldProps("email")}
        />
      </FormRow>
      <FormRow>
        <Input
          type="password"
          checkPasswordStrength={false}
          label="enter_your_password"
          error={errors?.password}
          {...getFieldProps("password")}
        />
      </FormRow>
      <div className="mt-4 flex justify-end">
        <Link
          href="/forgot-password"
          className="primary-color cursor-pointer text-xs font-normal"
        >
          {t("forgot_your_password")}
        </Link>
      </div>
      <Button
        type="submit"
        disabled={isFormDisabled}
        className="mt-[1.875rem] w-full"
      >
        {t("login")}
      </Button>
      <div className="mt-[1.25rem] flex w-full justify-center">
        <p>
          <span className="mr-1 text-base font-normal text-black">
            {t("dont_have_account")}
          </span>
          <Link
            href="/sign-up"
            className="primary-color cursor-pointer text-base font-normal"
          >
            {t("register")}
          </Link>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;
